import { useEffect } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { UPDATE_ORDER } from "../../graphql/customMutations";
import { orderPlacedPath } from "../main";
import { FullScreenSpinner } from "../../tabin/components/fullScreenSpinner";
import { toLocalISOString } from "../../util/util";
import { useErrorLogging } from "../../context/errorLogging-context";

const ONLINE_PAYMENT_FAILED_PREFIX = "ONLINE_PAYMENT_FAILED";
const ONLINE_PAYMENT_SUCCESS_TYPE = "ONLINE_PAYMENT_SUCCESS";
const EFTPOS_LOG_RETENTION_SECONDS = 90 * 24 * 60 * 60;

const getGatewayFailureReason = (reason: unknown, fallback: string) => {
    if (typeof reason === "string" && reason.trim()) return reason.trim();
    if (reason && typeof reason === "object" && "message" in reason && typeof (reason as { message?: unknown }).message === "string") {
        const message = (reason as { message: string }).message.trim();
        if (message) return message;
    }
    return fallback;
};

const getOnlinePaymentFailedCancellationReason = (reason: unknown, fallback: string) =>
    `${ONLINE_PAYMENT_FAILED_PREFIX} - ${getGatewayFailureReason(reason, fallback)}`;

export const clearWindcavePendingSession = () => {
    sessionStorage.removeItem("windcavePending");
};

export const WindcavePaymentCallback = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [updateOrderMutation] = useMutation(UPDATE_ORDER);
    const { addEftposLog } = useErrorLogging();

    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get("status");
    const sessionId = queryParams.get("sessionId");

    useEffect(() => {
        const handleResult = async () => {
            const { orderId, placedAt, amount, restaurantId, orderType, tableNumber, orderScheduledAt } = JSON.parse(
                sessionStorage.getItem("windcavePending") || "{}",
            );

            const cancelOrder = async (reason: unknown, fallbackReason: string, rawGatewayResponse: unknown, failureStep: string) => {
                if (!orderId || !placedAt) return;
                try {
                    const now = new Date();
                    const cancellationReason = getOnlinePaymentFailedCancellationReason(reason, fallbackReason);

                    await updateOrderMutation({
                        variables: {
                            orderId,
                            status: "CANCELLED",
                            paid: false,
                            paymentInProgress: false,
                            onlineOrder: true,
                            cancellationReason,
                            placedAt,
                            cancelledAt: toLocalISOString(now),
                            cancelledAtUtc: now.toISOString(),
                        },
                    });

                    if (restaurantId) {
                        await addEftposLog({
                            eftposProvider: "WINDCAVE_ONLINE",
                            amount: amount || 0,
                            type: ONLINE_PAYMENT_FAILED_PREFIX,
                            payload: JSON.stringify({
                                orderId,
                                provider: "WINDCAVE",
                                cancellationReason,
                                rawGatewayResponse,
                                failureStep,
                                sessionId,
                            }),
                            restaurantId,
                            timestamp: toLocalISOString(now),
                            expiry: Math.floor(now.getTime() / 1000) + EFTPOS_LOG_RETENTION_SECONDS,
                        });
                    }
                } catch (e) {
                    console.log("xxx...WindcavePaymentCallback: failed to cancel order:", e);
                }
            };

            if (status !== "approved") {
                const fallbackReason = status === "declined" ? "Payment declined" : "Payment cancelled";
                let gatewayReason: unknown = fallbackReason;
                let rawGatewayResponse: unknown = { callbackStatus: status, sessionId };

                if (sessionId) {
                    try {
                        const response = await axios.post(process.env.REACT_APP_CONFIRM_ONLINE_PAYMENT_WINDCAVE_URL!, { sessionId, restaurantId });
                        rawGatewayResponse = { callbackStatus: status, sessionId, confirmation: response.data };

                        if (response.data.authorised) {
                            if (restaurantId) {
                                const now = new Date();
                                await addEftposLog({
                                    eftposProvider: "WINDCAVE_ONLINE",
                                    amount: amount || 0,
                                    type: ONLINE_PAYMENT_SUCCESS_TYPE,
                                    payload: JSON.stringify({
                                        orderId,
                                        provider: "WINDCAVE",
                                        rawGatewayResponse: {
                                            callbackStatus: status,
                                            sessionId,
                                            confirmation: response.data,
                                        },
                                        successStep: "windcaveCallbackStatus",
                                    }),
                                    restaurantId,
                                    timestamp: toLocalISOString(now),
                                    expiry: Math.floor(now.getTime() / 1000) + EFTPOS_LOG_RETENTION_SECONDS,
                                });
                            }

                            clearWindcavePendingSession();
                            navigate(orderPlacedPath, { state: { orderType, tableNumber, orderScheduledAt, orderId } });
                            return;
                        }

                        gatewayReason = response.data.reason || response.data.error || fallbackReason;
                    } catch (err: any) {
                        rawGatewayResponse = {
                            callbackStatus: status,
                            sessionId,
                            confirmationError: {
                                status: err?.response?.status,
                                data: err?.response?.data,
                                message: err?.message,
                            },
                        };
                        gatewayReason = err?.response?.data?.reason || err?.response?.data?.error || fallbackReason;
                    }
                }

                await cancelOrder(gatewayReason, fallbackReason, rawGatewayResponse, "windcaveCallbackStatus");
                clearWindcavePendingSession();
                alert(status === "declined" ? "Payment was declined. Please try again." : "Payment was cancelled.");
                if (restaurantId) window.location.href = `${window.location.origin}/${restaurantId}`;
                return;
            }

            if (!sessionId) {
                clearWindcavePendingSession();
                alert("Invalid payment callback. Please contact Tabin support on 0800 006 101.");
                return;
            }

            if (!orderId) {
                clearWindcavePendingSession();
                alert("Payment session expired. Please place your order again.");
                window.location.href = window.location.origin;
                return;
            }

            try {
                const response = await axios.post(process.env.REACT_APP_CONFIRM_ONLINE_PAYMENT_WINDCAVE_URL!, { sessionId, restaurantId, amount: amount || 0 });
                const authorised = response.data.authorised;

                if (!authorised) {
                    await cancelOrder(
                        response.data.reason || response.data.error,
                        "Payment was not authorised by Windcave",
                        { sessionId, confirmation: response.data },
                        "confirmOnlinePaymentWindcave",
                    );
                    clearWindcavePendingSession();
                    alert("Payment was not authorised by Windcave. Please try again.");
                    window.location.href = `${window.location.origin}/${restaurantId}`;
                    return;
                }

                if (restaurantId) {
                    const now = new Date();
                    await addEftposLog({
                        eftposProvider: "WINDCAVE_ONLINE",
                        amount: amount || 0,
                        type: ONLINE_PAYMENT_SUCCESS_TYPE,
                        payload: JSON.stringify({
                            orderId,
                            provider: "WINDCAVE",
                            rawGatewayResponse: {
                                sessionId,
                                confirmation: response.data,
                            },
                            successStep: "confirmOnlinePaymentWindcave",
                        }),
                        restaurantId,
                        timestamp: toLocalISOString(now),
                        expiry: Math.floor(now.getTime() / 1000) + EFTPOS_LOG_RETENTION_SECONDS,
                    });
                }

                clearWindcavePendingSession();

                navigate(orderPlacedPath, { state: { orderType, tableNumber, orderScheduledAt, orderId } });
            } catch (err: any) {
                console.log("xxx...WindcavePaymentCallback error:", err);
                // On a server error (5xx), the payment may have succeeded at Windcave's end.
                // Do NOT cancel the order — leave it in paymentInProgress state for manual review.
                if (err?.response?.status >= 500) {
                    clearWindcavePendingSession();
                    alert(
                        "We couldn't verify your payment due to a server error. Please contact support on 0800 006 101 with your order reference: " +
                            orderId,
                    );
                    return;
                }
                // For network/unknown errors, same treatment.
                clearWindcavePendingSession();
                alert("Something went wrong processing your payment. Please contact support on 0800 006 101.");
            }
        };

        handleResult();
    }, []);

    return <FullScreenSpinner show={true} text="Processing your payment..." />;
};
